import type { LateCancelCountsDto, LateCancelListStatus, LateCancelRecordDto } from '@shared/core/cancellation-policy'
import { formatInTimeZone } from 'date-fns-tz'
import parsePhoneNumberFromString from 'libphonenumber-js'

export const LATE_CANCEL_DATE_FORMATS = {
  appointment: "EEEE, MMMM d '@' h:mmaaa",
  cancelled: "EEE, MMM d '@' h:mmaaa",
  ownerMarked: 'EEE, MMM d',
  resolved: 'MMM d',
} as const

export type LateCancelsEmptyState = 'turn_on' | 'none_yet' | 'all_caught_up' | 'none_resolved'

function isKnownTimeZone(zone: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: zone })
    return true
  } catch {
    return false
  }
}

/** Every time on a record shows in the zone recorded with it, not the viewer's. */
export function formatInRecordZone(iso: string, zone: string | null, pattern: string): string {
  const timeZone = zone && isKnownTimeZone(zone) ? zone : Intl.DateTimeFormat().resolvedOptions().timeZone
  return formatInTimeZone(new Date(iso), timeZone, pattern)
}

export function formatLeadTime(record: Pick<LateCancelRecordDto, 'occurred_at' | 'appointment_start_at'>): string {
  const elapsedMs = Date.parse(record.appointment_start_at) - Date.parse(record.occurred_at)
  const totalMinutes = Math.max(0, Math.floor(elapsedMs / 60_000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours === 0) return `${minutes} min before start`
  if (minutes === 0) return `${hours} h before start`
  return `${hours} h ${minutes} min before start`
}

export function formatClientPhone(phone: string): string {
  return parsePhoneNumberFromString(phone)?.formatInternational() ?? phone
}

export function describeClientPhones(phones: string[]): { first: string | null; extraCount: number } {
  if (phones.length === 0) return { first: null, extraCount: 0 }
  return { first: formatClientPhone(phones[0]), extraCount: phones.length - 1 }
}

export function lateCancelClientLabel(record: Pick<LateCancelRecordDto, 'client_name' | 'client_phones'>): string {
  const name = record.client_name?.trim()
  if (name) return name
  return record.client_phones.length > 0 ? formatClientPhone(record.client_phones[0]) : 'Client'
}

/** First match wins (late-cancel-records spec, "Late-cancel list page"). */
export function pickLateCancelsEmptyState(
  counts: LateCancelCountsDto,
  status: LateCancelListStatus,
): LateCancelsEmptyState {
  if (counts.open + counts.resolved === 0) return counts.policyOn ? 'none_yet' : 'turn_on'
  return status === 'open' ? 'all_caught_up' : 'none_resolved'
}
